import React, { ReactNode, createContext, useContext, useEffect, useReducer } from "react";

import { ConfigStateContext } from "../Configurations/context";
import { Filters } from "./model";

export const FiltersStateContext = createContext(null);
export const FiltersDispatchContext = createContext(null);

const defaultState: Filters = {
	page: {
		value: 1,
		text: "Pagina 1",
	},
	order: {
		value: 2,
		text: "Popularidad",
	},
};

/** Backend may send garage or hasGarage (e.g. value: 1). Normalize to garage with boolean value for context/UI. */
function normalizeGarage(initialState: Filters & { hasGarage?: { value?: unknown; text?: string } }): Partial<Filters> {
	if (!initialState) return {};
	const source = initialState.garage ?? initialState.hasGarage;
	if (source == null) return {};
	const value = source?.value;
	const isTruthy = value === true || value === 1 || value === "1";
	if (!isTruthy) return { garage: null };
	return { garage: { value: true, text: source?.text ?? "con Garaje" } };
}

function normalizeInitialState(initialState: Filters & { hasGarage?: { value?: unknown; text?: string } }): Filters {
	if (!initialState) return defaultState;
	const { hasGarage: _drop, ...rest } = initialState as Filters & { hasGarage?: unknown };
	return { ...rest, ...normalizeGarage(initialState) } as Filters;
}

const reducer = (state: Filters, action): Filters => {
	switch (action.type) {
		case "merge":
			if (!("page" in action.payload)) {
				action.payload.page = { value: 1, text: "Pagina 1" };
			}

			return { ...state, ...action.payload };
		case "set":
			if (!("page" in action.payload)) {
				action.payload.page = { value: 1, text: "Pagina 1" };
			}

			return { ...action.payload };
		case "reset":
			return { ...defaultState };
		default:
			throw new Error("no funciona");
	}
};

export const FiltersContextProvider = ({
	children,
	initialState = null,
	active,
	callback,
}: {
	children: ReactNode;
	initialState: Filters;
	active: boolean;
	callback?: (state) => void;
}) => {
	if (!active) return <>{children}</>;

	const [state, dispatch] = useReducer(reducer, { ...defaultState, ...normalizeInitialState(initialState) });
	const { country_code } = useContext(ConfigStateContext);

	useEffect(() => {
		if (!initialState) return;
		let forceTemporalInCEEE =
			country_code == "ce3"
				? { text: "Alquiler Temporal", value: 4 }
				: state.operation_type_id;

		dispatch({
			type: "merge",
			payload: {
				projects: state.projects,
				page: state.page,
				operation_type_id: forceTemporalInCEEE,
			},
		});
	}, [initialState, country_code]);

	useEffect(() => {
		if (typeof callback == "function") callback(state);
	}, [state]);

	return (
		<FiltersDispatchContext.Provider value={dispatch}>
			<FiltersStateContext.Provider value={state}>{children}</FiltersStateContext.Provider>
		</FiltersDispatchContext.Provider>
	);
};

